// statementPdf.js (P4-CC10) — branded Customer Statement PDF (jsPDF + jspdf-autotable).
// Consumes the getCustomerStatement result + Business Settings; mirrors StatementView.
// ALL dynamic text (doc.text args + autotable cells) is routed through asciiPdf(A) —
// jsPDF's WinAnsi Helvetica can't render en/em dashes, arrows, smart quotes, etc.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { COLORS, money, businessBrand, brandedHeader, brandedFooter, asciiPdf as A } from "./pdfBrand";
import { todayISO } from "./dates";

/** Build (but don't save) the jsPDF statement document. */
export function buildStatementDoc(statement, settings) {
  const s = statement || {};
  const cust = s.customer || {};
  const B = businessBrand(settings);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 14, rightX = pageW - M;

  let y = brandedHeader(doc, { title: "STATEMENT", docNumber: cust.company_name || "Statement", business: B });

  // FROM (left)
  doc.setTextColor(...COLORS.GREY); doc.setFontSize(8); doc.setFont("helvetica", "bold");
  doc.text(A("FROM"), M, y);
  doc.setFont("helvetica", "normal"); doc.setTextColor(...COLORS.NAVY); doc.setFontSize(9);
  let by = y + 5;
  [B.name, ...B.addressLines, B.email, B.vat ? `VAT: ${B.vat}` : null].filter(Boolean).forEach((line) => { doc.text(A(line), M, by); by += 4.5; });

  // Statement meta (right)
  const metaX = pageW - M - 70;
  doc.setFillColor(...COLORS.LIGHT); doc.roundedRect(metaX, y - 4, 70, 18, 2, 2, "F");
  const metaRow = (label, value, row) => {
    const ry = y + 1 + row * 6;
    doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.GREY); doc.setFontSize(7.5);
    doc.text(A(label), metaX + 3, ry);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...COLORS.NAVY); doc.setFontSize(9);
    doc.text(A(value || "-"), rightX - 3, ry, { align: "right" });
  };
  const periodLabel = (s.period && (s.period.from || s.period.to)) ? `${s.period.from || "..."} to ${s.period.to || "..."}` : "All time";
  metaRow("STATEMENT DATE", (s.period && s.period.to) || todayISO(), 0);
  metaRow("PERIOD", periodLabel, 1);

  y = Math.max(by, y + 18) + 4;

  // STATEMENT FOR (customer)
  doc.setFillColor(...COLORS.LIGHT); doc.roundedRect(M, y, pageW - 2 * M, 24, 2, 2, "F");
  doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.GREY); doc.setFontSize(7.5);
  doc.text(A("STATEMENT FOR"), M + 4, y + 6);
  doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.NAVY); doc.setFontSize(11);
  doc.text(A(`${cust.company_name || "-"}${cust.customer_number ? `  ·  ${cust.customer_number}` : ""}`), M + 4, y + 12);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...COLORS.GREY);
  const custLines = [cust.address ? String(cust.address).split("\n")[0] : null, cust.email, cust.vat_number ? `VAT: ${cust.vat_number}` : null].filter(Boolean);
  let cy = y + 17; custLines.forEach((l) => { doc.text(A(l), M + 4, cy); cy += 4.2; });
  y += 30;

  // Transactions (opening -> txns -> closing)
  const txns = s.transactions || [];
  const body = [
    [s.period?.from || "", "", "Opening balance", "", "", money(s.opening_balance)],
    ...txns.map((t) => [t.date || "", t.ref || "", t.description || t.type, t.debit ? money(t.debit) : "", t.credit ? money(t.credit) : "", money(t.running_balance)]),
    ["", "", "Closing balance", "", "", money(s.closing_balance)],
  ].map((r) => r.map(A));
  autoTable(doc, {
    startY: y,
    head: [["Date", "Reference", "Description", "Debit", "Credit", "Balance"].map(A)],
    body,
    margin: { left: M, right: M },
    styles: { fontSize: 8.5, cellPadding: 2.5, lineColor: [226, 232, 240], lineWidth: 0.1, textColor: COLORS.NAVY },
    headStyles: { fillColor: COLORS.NAVY, textColor: 255, fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 28 }, 2: { cellWidth: "auto" }, 3: { cellWidth: 24, halign: "right" }, 4: { cellWidth: 24, halign: "right" }, 5: { cellWidth: 26, halign: "right" } },
    didParseCell: (data) => { if (data.section === "body" && (data.row.index === 0 || data.row.index === body.length - 1)) data.cell.styles.fontStyle = "bold"; },
  });
  y = doc.lastAutoTable.finalY + 8;

  const ensureSpace = (needed) => { if (y + needed > pageH - 14) { doc.addPage(); y = 18; } };
  ensureSpace(40);

  // Total outstanding / In credit bar
  const inCredit = s.in_credit || s.current_outstanding < 0;
  const barX = pageW - M - 80;
  doc.setFillColor(...COLORS.BLUE); doc.roundedRect(barX - 3, y - 4.5, 83, 9, 1.5, 1.5, "F");
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(A(inCredit ? "IN CREDIT" : "TOTAL OUTSTANDING"), barX, y + 1.5);
  doc.text(A(money(Math.abs(s.current_outstanding))), rightX - 1, y + 1.5, { align: "right" });
  y += 16;

  // Aging
  ensureSpace(24);
  const a = s.aging || {};
  doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.GREY); doc.setFontSize(8);
  doc.text(A("AGING SUMMARY"), M, y); y += 2;
  autoTable(doc, {
    startY: y,
    head: [["Current", "1-30", "31-60", "61-90", "90+", "Total"].map(A)],
    body: [[money(a.current), money(a.d1_30), money(a.d31_60), money(a.d61_90), money(a.d90_plus), money(a.total)].map(A)],
    margin: { left: M, right: M },
    styles: { fontSize: 8.5, cellPadding: 2.5, halign: "right", lineColor: [226, 232, 240], lineWidth: 0.1, textColor: COLORS.NAVY },
    headStyles: { fillColor: COLORS.NAVY, textColor: 255, fontStyle: "bold", fontSize: 8, halign: "right" },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 4) data.cell.styles.textColor = COLORS.DANGER;   // 90+ emphasised
      if (data.section === "body" && data.column.index === 5) data.cell.styles.fontStyle = "bold";
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  if (inCredit) {
    ensureSpace(10);
    doc.setFont("helvetica", "italic"); doc.setTextColor(...COLORS.GREY); doc.setFontSize(8.5);
    doc.text(A(`This account is in credit by ${money(Math.abs(s.current_outstanding))}. No payment is due.`), M, y);
  }

  brandedFooter(doc, B);
  return doc;
}

/** Build + trigger a browser download. */
export function downloadStatementPDF(statement, settings) {
  const doc = buildStatementDoc(statement, settings);
  const name = (statement?.customer?.company_name || "customer").replace(/[^\w-]+/g, "_");
  doc.save(`Statement-${name}.pdf`);
}
